import React from "react";
import { Loader2, Sparkles, Anchor, Ruler, Circle, Shirt, Layout } from "lucide-react";

export const ConfigPanel = ({
  mode,
  setMode,
  anchor,
  setAnchor,
  multiplier,
  setMultiplier,
  startingStitches,
  setStartingStitches,
  targetSize,
  setTargetSize,
  gaugeStitches,
  setGaugeStitches,
  gaugeRows,
  setGaugeRows,
  mainStitchPattern,
  setMainStitchPattern,
  modelName,
  setModelName,
  globalNotes,
  setGlobalNotes,
  onGenerate,
  loading,
  canGenerate
}) => {
  return (
    <div className="flex flex-col gap-5" data-testid="config-panel">
      {/* Mode Switcher Tab Buttons */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400">
          Workshop Mode
        </label>
        <div className="grid grid-cols-2 gap-1 bg-zinc-100 dark:bg-zinc-800/80 p-1 rounded-lg">
          <button
            data-testid="mode-amigurumi-btn"
            type="button"
            onClick={() => setMode("amigurumi")}
            className={`py-1.5 text-xs font-semibold rounded-md transition-all ${
              mode === "amigurumi"
                ? "bg-white dark:bg-zinc-700 text-zinc-950 dark:text-zinc-50 shadow-sm"
                : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
            }`}
          >
            Amigurumi
          </button>
          <button
            data-testid="mode-garment-btn"
            type="button"
            onClick={() => setMode("garment")}
            className={`py-1.5 text-xs font-semibold rounded-md transition-all ${
              mode === "garment"
                ? "bg-white dark:bg-zinc-700 text-zinc-950 dark:text-zinc-50 shadow-sm"
                : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
            }`}
          >
            Garments
          </button>
        </div>
      </div>

      {/* Model Selector Dropdown */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400">
          AI Forge Model
        </label>
        <select
          data-testid="model-selector"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300"
        >
          <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fast / High Quota)</option>
          <option value="gemini-3.5-flash">Gemini 3.5 Flash (Expert / Low Quota)</option>
        </select>
        <p className="text-[10px] text-zinc-400 -mt-1">
          Use 2.5 for drafts and edits. Switch to 3.5 for complex final pattern analysis.
        </p>
      </div>

      {mode === "amigurumi" ? (
        <>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Anchor className="h-3.5 w-3.5" /> Max Stitch Count (Anchor)
            </label>
            <input
              data-testid="anchor-input"
              type="number"
              min="1"
              value={anchor}
              onChange={(e) => setAnchor(e.target.value)}
              className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 font-mono"
            />
            <p className="text-xs text-zinc-400">Widest round of the main body geometry.</p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Circle className="h-3.5 w-3.5" /> Starting Stitches (Magic Circle)
            </label>
            <input
              data-testid="starting-stitches-input"
              type="number"
              min="1"
              value={startingStitches}
              onChange={(e) => setStartingStitches(e.target.value)}
              className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 font-mono"
            />
            <p className="text-xs text-zinc-400">Default 6. Stitch count of the first round.</p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Ruler className="h-3.5 w-3.5" /> Scale Multiplier
            </label>
            <input
              data-testid="multiplier-input"
              type="number"
              min="0.1"
              step="0.1"
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value)}
              className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 font-mono"
            />
            <p className="text-xs text-zinc-400">Default 1.0. Values &gt;1 enlarge, &lt;1 shrink.</p>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Shirt className="h-3.5 w-3.5" /> Target Size
            </label>
            <select
              data-testid="target-size-input"
              value={targetSize}
              onChange={(e) => setTargetSize(e.target.value)}
              className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300"
            >
              <option value="S">Small (S)</option>
              <option value="M">Medium (M)</option>
              <option value="L">Large (L)</option>
              <option value="XL">Extra Large (XL)</option>
              <option value="Custom">Custom / Oversized</option>
            </select>
            <p className="text-xs text-zinc-400">Standard sizing blueprint target.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
                <Ruler className="h-3.5 w-3.5" /> Stitches per 4"
              </label>
              <input
                data-testid="gauge-stitches-input"
                type="number"
                min="1"
                value={gaugeStitches}
                onChange={(e) => setGaugeStitches(e.target.value)}
                className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 font-mono"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
                <Ruler className="h-3.5 w-3.5" /> Rows per 4"
              </label>
              <input
                data-testid="gauge-rows-input"
                type="number"
                min="1"
                value={gaugeRows}
                onChange={(e) => setGaugeRows(e.target.value)}
                className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 font-mono"
              />
            </div>
          </div>
          <p className="text-xs text-zinc-400 -mt-2">Your gauge swatch stitch density.</p>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Layout className="h-3.5 w-3.5" /> Main Stitch Pattern
            </label>
            <input
              data-testid="main-stitch-input"
              type="text"
              value={mainStitchPattern}
              onChange={(e) => setMainStitchPattern(e.target.value)}
              className="flex h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300"
              placeholder="e.g. double crochet, granny cluster"
            />
            <p className="text-xs text-zinc-400">Primary stitch texture used for panels.</p>
          </div>
        </>
      )}

      {/* Global Drafting Notes Field */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium tracking-[0.04em] uppercase text-zinc-500 dark:text-zinc-400">
          Global Drafting Notes
        </label>
        <textarea
          data-testid="global-notes-input"
          rows={3}
          value={globalNotes}
          onChange={(e) => setGlobalNotes(e.target.value)}
          placeholder="e.g. Base pieces start at 10 sc in mc, except horn [4 sc in mc-- ends at 10 sc]"
          className="flex w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-300 resize-none font-sans"
        />
        <p className="text-[10px] text-zinc-400 -mt-1">
          Special rules or constraints the AI should respect across all components.
        </p>
      </div>

      <button
        data-testid="generate-button"
        onClick={onGenerate}
        disabled={!canGenerate || loading}
        className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium bg-zinc-900 text-zinc-50 hover:bg-zinc-800 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-white h-11 px-4 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" /> Generate Pattern
          </>
        )}
      </button>
      {mode === "amigurumi" && (
        <p className="text-[10px] text-zinc-400 -mt-3">
          Uses two AI passes: one to record visual proportions, then one to draft against those expectations.
        </p>
      )}
    </div>
  );
};
